import { Facepile } from "@dropbox/dig-components/avatar";
import { Button } from "@dropbox/dig-components/buttons";
import { Card } from "@dropbox/dig-components/card";
import { Text } from "@dropbox/dig-components/typography";
import { Split } from "@dropbox/dig-foundations";
import { UIIcon } from "@dropbox/dig-icons";
import {
  CloseLine,
  GlobeLine,
  LockLine,
  PersonMultipleLine,
  StacksLine,
  TeamLine,
  Twinkle2Line,
} from "@dropbox/dig-icons/dist/mjs/assets";
import i18n from "@mirage/translations";
import classNames from "classnames";
import React from "react";
import styles from "./CurateTooltip.module.css";
import avatar1 from "./media/avatar1.png";
import avatar2 from "./media/avatar2.png";
import avatar4 from "./media/avatar4.png";
import left_collage from "./media/left_collage.png";
import middle_collage from "./media/middle_collage.png";
import right_collage from "./media/right_collage.png";

interface CurateTooltipProps {
  onClose: () => void;
}

export const CurateTooltip = ({ onClose }: CurateTooltipProps) => {
  return (
    <>
      <Card
        hasBorders={false}
        borderRadius="Large"
        className={styles.CurateTooltipCardContainer}
      >
        <Card.Content
          backgroundColor="Subtle"
          className={styles.CurateTooltipCardTop}
        >
          <div className={styles.CurateTooltipCardHeader}>
            <Button onClick={() => onClose()} variant="borderless">
              <UIIcon src={CloseLine} />
            </Button>
          </div>
          <div className={styles.CurateTooltipCardTitle}>
            <div className={styles.CurateTooltipStartCard}>
              <CurateTooltipCard
                image={
                  <img
                    src={left_collage}
                    className={styles.CurateTooltipImgSmallest}
                    alt={i18n.t("media_collage")}
                  />
                }
                small
                title={i18n.t("media_curate_branding")}
                subtitle={i18n.t("media_curate_invite_only")}
                rotate="-6deg"
                subtitleIcon={LockLine}
              />
            </div>
            <div className={styles.CurateTooltipMiddleCard}>
              <CurateTooltipCard
                image={
                  <img
                    src={middle_collage}
                    className={styles.CurateTooltipImg}
                    alt={i18n.t("media_collage")}
                  />
                }
                title={i18n.t("media_curate_may_campaign")}
                subtitle={i18n.t("media_curate_internal")}
                rotate="0"
                subtitleIcon={TeamLine}
              />
            </div>
            <div className={styles.CurateTooltipEndCard}>
              <CurateTooltipCard
                image={
                  <img
                    src={right_collage}
                    className={styles.CurateTooltipImgSmall}
                    alt={i18n.t("media_collage")}
                  />
                }
                small
                title={i18n.t("media_curate_social_media")}
                subtitle={i18n.t("media_curate_public")}
                rotate="5deg"
                subtitleIcon={GlobeLine}
              />
            </div>
          </div>
        </Card.Content>
        <Card.Content
          backgroundColor="Base"
          className={classNames(
            styles.CurateTooltipCardBottom,
            styles.FlexColumnCentered,
          )}
        >
          <Text variant="label" size="large" isBold>
            {i18n.t("media_curate_title")}
          </Text>
          <div className={styles.FlexColumnAlignLeft}>
            <CurateTooltipListText
              prefixText={i18n.t("media_curate_organize")}
              text={i18n.t("media_curate_organize_text")}
              icon={PersonMultipleLine}
            />
            <CurateTooltipListText
              prefixText={i18n.t("media_curate_collaborate")}
              text={i18n.t("media_curate_collaborate_text")}
              icon={StacksLine}
            />
            <CurateTooltipListText
              prefixText={i18n.t("media_curate_go_beyond_images")}
              text={i18n.t("media_curate_go_beyond_images_text")}
              icon={Twinkle2Line}
            />
          </div>
        </Card.Content>
      </Card>
    </>
  );
};

interface CurateTooltipCardProps {
  image: React.ReactNode;
  title: string;
  small?: boolean;
  subtitle: string;
  rotate: string;
  subtitleIcon: React.ComponentType<React.SVGAttributes<SVGElement>>;
}

const CurateTooltipCard = ({
  image,
  small,
  title,
  subtitle,
  rotate,
  subtitleIcon,
}: CurateTooltipCardProps) => {
  return (
    <Card
      borderRadius="Large"
      className={classNames(styles.CurateTooltipCard, {
        [styles.CurateTooltipCardSmall]: small,
      })}
      style={{ rotate: rotate }}
    >
      <Card.Content
        backgroundColor="Base"
        className={styles.CurateTooltipCardContents}
      >
        <div
          className={classNames(
            styles.CurateTooltipCardContents,
            styles.FlexColumnCentered,
          )}
        >
          {image}
          <Text variant="label" isBold size="small">
            {title}
          </Text>
          <Text size="xsmall" color="subtle">
            <Split gap="2">
              <UIIcon src={subtitleIcon} size="small" />
              {subtitle}
            </Split>
          </Text>
          <div className={styles.CurateTooltipCardAvatarContainer}>
            <Facepile size="xsmall">
              <Facepile.Item
                src={avatar1}
                alt={i18n.t("media_avatar_alt_text")}
              >
                AB
              </Facepile.Item>
              <Facepile.Item
                src={avatar2}
                alt={i18n.t("media_avatar_alt_text")}
              >
                CD
              </Facepile.Item>
              <Facepile.Item
                src={avatar4}
                alt={i18n.t("media_avatar_alt_text")}
              >
                DE
              </Facepile.Item>
              <Facepile.Item
                color="subtle"
                isInactive
                alt={i18n.t("media_avatar_alt_text")}
              >
                {small
                  ? i18n.t("media_avatar_count_small")
                  : i18n.t("media_avatar_count_large")}
              </Facepile.Item>
            </Facepile>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

interface CurateTooltipListTextProps {
  prefixText?: string;
  text: string;
  icon: React.ComponentType<React.SVGAttributes<SVGElement>>;
}

const CurateTooltipListText = ({
  prefixText,
  text,
  icon,
}: CurateTooltipListTextProps) => {
  return (
    <Split gap="Micro Medium">
      <UIIcon src={icon} />
      <span>
        <Text isBold color="subtle">
          {prefixText}
        </Text>
        &nbsp;
        <Text>{text}</Text>
      </span>
    </Split>
  );
};
